import React, { Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import Icon from '../components/Icon';
import { DestinationCityStateZip } from '../components/DestinationAddress';
import TrackingNumberLink from '../components/TrackingNumberLink';
import TrackingEvents from '../components/TrackingEvents';
import { useTrackingData, useShipmentData } from '../components/DataProviders';
import CarrierIcon from '../components/CarrierIcon';


const useTrackingStyles = makeStyles( theme => ({
  root: {
    border: '1px solid #ccc',
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column'
  },
  summary: {
    padding: '6px',
    lineHeight: '24px'
  },
  label: {
    fontWeight: 'bold',
    display: 'inline-block'
  },
  value: {
    display: 'inline-block'
  },
  trackingLink: {
    display: 'inline-block',
    '& .external-link': {
      fontSize: '11px'
    }
  },
  carrierIcon: {
    height: '16px',
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  trackingEvents: {
    borderTop: '1px solid #ccc',
    flexGrow: '1',
    overflow: 'auto'
  },
  event: {
    padding: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: '13px',
    cursor: 'default',
  },
  eventWrapper: {
    color: '#ccc',
    borderBottom: '1px dotted #ccc',
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      background: '#f6f6f6'
    },
    '& .description': {
      fontWeight: '600',
      order: '1',
      width: '100%'
    },
    '& .location': {
      color: '#999',
      alignSelf: 'flex-end',
      marginLeft: 'auto'
    },
    '&.most-recent': {
      color: '#777'
    }
  },
  dayHeading: {
    background: '#F6F8F9',
    borderTop: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    fontSize: '13px',
    padding: '6px',
    position: 'relative',  // Bc we're using CSS to hide empty headers (repeats)
    top: '-1px',           // having borders that don't stack is hard, this just
    marginBottom: '-1px',  // covers the border above (and pulls under-content up for symmetry)
    '&:empty': {
      display: 'none'
    }
  },
  signatureRequired: {
    backgroundColor: '#d9edf7',
    borderColor: '#bce8f1',
    borderRadius: '4px',
    color: '#31708f',
    fontWeight: 'bold',
    padding: '2px 6px',
  },
  noTrackingMsg: {
    padding: '6px'
  }
}));


export default function TrackingDetails({ onEventHover, className='' }) {
  const classes = useTrackingStyles();
  const { carrier_events = [] } = useTrackingData();
  const { service_name, confirmation_requirement } = useShipmentData();

  let dateHeaders = new Set();
  const formattedDateHeader = (event) => {
    const content = moment(event.occurred_at).format('MMMM D, YYYY');
    if( dateHeaders.has(content) ) {
      return null;
    }
    else {
      dateHeaders.add(content);
      return content;
    }
  };

  const onMouseEnter = (trackingEvent) => () => onEventHover(trackingEvent);

  return (
    <div className={`${classes.root} ${className}`}>

      <div className={classes.summary}>
        <div>
          <div className={classes.label}>Shipping To:&nbsp;</div>
          <DestinationCityStateZip className={classes.value} />
        </div>

        { service_name &&
          <div>
            <div className={classes.label}>Service:&nbsp;</div>
            <CarrierIcon className={classes.carrierIcon} /> {service_name}
          </div>
        }

        <div>
          <div className={classes.label}>Tracking #:&nbsp;</div>
          <TrackingNumberLink className={classes.trackingLink} />
        </div>

        { confirmation_requirement &&
          <div className={classes.signatureRequired}>
            <Icon icon={faInfoCircle} />&nbsp;
            { confirmation_requirement === 'adult' ? (
              "Requires an adult's signature for delivery."
            ):(
              "Requires a signature for delivery."
            )}
          </div>
        }
      </div>

      <div className={classes.trackingEvents}>
        { (carrier_events.length === 0) ? (
          <div className={classes.noTrackingMsg}>
            Package tracking details will be available once your package has shipped.
          </div>
        ):(
          <TrackingEvents eventClassName={classes.event} timestampFormat="H:mm A" eachEvent={ ({content,event,key,index}) => (
            <Fragment key={key}>
              <div className={classes.dayHeading}>{formattedDateHeader(event)}</div>
              <div className={`${classes.eventWrapper} ${index === 0 && 'most-recent'}`} onMouseEnter={onMouseEnter(event)}>
                {content}
              </div>
            </Fragment>
          )} />
        )}
      </div>
    </div>
  );
}
